import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { AlertTriangle, Wrench, RefreshCw, Link2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Scans every event_timings doc tied to the user's competitions
 * and surfaces grade_timings rows whose stored grade_id no longer
 * matches a current grade. Offers three ways to fix each row:
 *   1. Auto-Repair — the "Apply N re-link(s)" footer button uses
 *      the legacy position-based proposal endpoint.
 *   2. Manual Link — per-row dropdown of live grades on the
 *      timing's (schedule, discipline). Applies immediately.
 *   3. Delete — per-row trash button; toast offers a 5-second Undo.
 *
 * Drops into ``EventTimingPage`` as a sticky banner. Hides itself
 * when there are no stale links. Re-scans after every action so
 * the counts stay honest.
 */
export default function StaleLinksBanner() {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Lazy-loaded candidate grade lists, keyed by timing_id.
  const [candidates, setCandidates] = useState({});
  // Per-row pending-action flags (timing_id::stored_grade_id → bool)
  const [busyRows, setBusyRows] = useState({});

  const runScan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/event-timings/stale-grades-scan`, {
        withCredentials: true,
      });
      setScan(res.data);
    } catch (err) {
      console.warn('Stale-link scan failed:', err?.response?.status);
      setScan({ total_stale_rows: 0, affected_timings: 0, summaries: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runScan(); }, [runScan]);

  // ───────────────────────────────── candidate fetcher
  const loadCandidates = useCallback(async (timingId) => {
    if (candidates[timingId]) return candidates[timingId];
    try {
      const res = await axios.get(
        `${API}/event-timings/${timingId}/candidate-grades`,
        { withCredentials: true },
      );
      setCandidates((prev) => ({ ...prev, [timingId]: res.data || [] }));
      return res.data || [];
    } catch (err) {
      toast.error('Could not load candidate grades.');
      return [];
    }
  }, [candidates]);

  // Eager-fetch candidates for every visible summary so dropdowns
  // are ready by the time the user opens them.
  useEffect(() => {
    if (!reviewOpen || !scan?.summaries) return;
    scan.summaries.forEach((s) => { loadCandidates(s.timing_id); });
  }, [reviewOpen, scan, loadCandidates]);

  const rowKey = (tid, sid) => `${tid}::${sid}`;
  const setRowBusy = (tid, sid, v) =>
    setBusyRows((prev) => ({ ...prev, [rowKey(tid, sid)]: v }));

  // ───────────────────────────────── per-row actions
  const handleRelink = async (timingId, storedGradeId, newBareGradeId, label) => {
    setRowBusy(timingId, storedGradeId, true);
    try {
      await axios.post(
        `${API}/event-timings/${timingId}/relink-row`,
        { stored_grade_id: storedGradeId, new_bare_grade_id: newBareGradeId },
        { withCredentials: true },
      );
      toast.success(`Row linked to "${label}"`);
      await runScan();
    } catch (err) {
      toast.error('Relink failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRowBusy(timingId, storedGradeId, false);
    }
  };

  const handleDelete = async (timingId, storedGradeId, rowLabel) => {
    setRowBusy(timingId, storedGradeId, true);
    try {
      const res = await axios.post(
        `${API}/event-timings/${timingId}/delete-row`,
        { stored_grade_id: storedGradeId },
        { withCredentials: true },
      );
      const deletedRow = res.data?.deleted_row;
      toast.success(`Removed "${rowLabel || storedGradeId}"`, {
        duration: 5000,
        action: deletedRow ? {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.post(
                `${API}/event-timings/${timingId}/restore-row`,
                { row: deletedRow },
                { withCredentials: true },
              );
              toast.success('Row restored');
              runScan();
            } catch (e) {
              toast.error('Undo failed: ' + (e.response?.data?.detail || e.message));
            }
          },
        } : undefined,
      });
      await runScan();
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRowBusy(timingId, storedGradeId, false);
    }
  };

  // ───────────────────────────────── batch repair (legacy)
  const handleRepairAll = async () => {
    if (!scan?.summaries?.length) return;
    setRepairing(true);
    let totalRepaired = 0;
    let totalUnmatched = 0;
    try {
      for (const s of scan.summaries) {
        const r = await axios.post(
          `${API}/event-timings/${s.timing_id}/repair-stale-grades`,
          {}, { withCredentials: true },
        );
        totalRepaired += r.data.repaired || 0;
        totalUnmatched += r.data.unmatched || 0;
      }
      if (totalRepaired > 0) {
        toast.success(
          `Re-linked ${totalRepaired} stale grade row(s)` +
          (totalUnmatched > 0
            ? `. ${totalUnmatched} couldn't be auto-matched — link or delete manually.`
            : ''),
        );
      } else if (totalUnmatched > 0) {
        toast.warning(
          `${totalUnmatched} stale row(s) couldn't be auto-matched. Link or delete manually.`,
        );
      }
      await runScan();
    } catch (err) {
      toast.error('Repair failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRepairing(false);
    }
  };

  const autoMatchableCount = useMemo(() => {
    if (!scan?.summaries) return 0;
    return scan.summaries.reduce(
      (acc, s) => acc + (s.stale_rows || []).filter((r) => r.proposed_grade_id).length,
      0,
    );
  }, [scan]);

  if (loading && !scan) return null;
  if (!scan || scan.total_stale_rows === 0) return null;

  return (
    <>
      <Alert
        className="border-amber-300 bg-amber-50 text-amber-900"
        data-testid="stale-links-banner"
      >
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="font-semibold">
          {scan.total_stale_rows} grade timing row(s) have stale links
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <p className="text-sm">
            These rows reference grades that have been deleted or re-created,
            so the OOS engine silently uses default values for affected
            events. Re-linking will preserve every timing value you&apos;ve
            entered — only the internal grade reference changes.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              variant="default"
              onClick={() => setReviewOpen(true)}
              disabled={repairing}
              data-testid="open-review-btn"
            >
              <Wrench className="h-4 w-4 mr-1.5" />
              Review &amp; Repair
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={runScan}
              disabled={loading || repairing}
              data-testid="rescan-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Rescan
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Stale Grade Links</DialogTitle>
            <DialogDescription>
              Each row can be auto-mapped (where a proposal exists), manually
              linked to any current grade in the same discipline, or removed
              entirely. Removed rows can be restored from the toast within
              5&nbsp;seconds.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {scan.summaries.map((s) => (
              <Card key={s.timing_id} data-testid={`summary-${s.timing_id}`}>
                <CardContent className="pt-4">
                  <p className="font-semibold mb-2">
                    {s.event_type}{' '}
                    <span className="text-sm text-muted-foreground font-normal">
                      ({s.stale_count} stale / {s.healthy_count} healthy)
                    </span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-stone-200 text-left">
                        <tr>
                          <th className="py-1.5 px-2 font-medium">Grade · Round</th>
                          <th className="py-1.5 px-2 font-medium">Time/Skater</th>
                          <th className="py-1.5 px-2 font-medium">Auto-proposed</th>
                          <th className="py-1.5 px-2 font-medium">Manual Link</th>
                          <th className="py-1.5 px-2 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.stale_rows.map((r, i) => {
                          const busy = !!busyRows[rowKey(s.timing_id, r.stored_grade_id)];
                          const rowLabel = r.original_grade_name
                            || `R${r.round_num} · ${r.bare_grade_id}`;
                          const cands = candidates[s.timing_id] || [];
                          return (
                            <tr
                              key={`${s.timing_id}-${i}`}
                              className="border-b border-stone-100 align-top"
                              data-testid={`stale-row-${s.timing_id}-${r.stored_grade_id}`}
                            >
                              <td className="py-1.5 px-2">
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {r.original_grade_name || (
                                      <span className="text-stone-500 italic">
                                        (name unknown)
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-xs text-stone-500">
                                    R{r.round_num}
                                    {r.bare_grade_id && (
                                      <span className="ml-1 font-mono">
                                        · {r.bare_grade_id}
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className="py-1.5 px-2">{r.time_per_skater || '—'}</td>
                              <td className="py-1.5 px-2">
                                {r.proposed_grade_name ? (
                                  <span className="text-emerald-700">
                                    {r.proposed_grade_name}
                                  </span>
                                ) : (
                                  <span className="text-stone-500 italic">none</span>
                                )}
                              </td>
                              <td className="py-1.5 px-2 min-w-[200px]">
                                <Select
                                  disabled={busy || cands.length === 0}
                                  onValueChange={(val) => {
                                    const target = cands.find((c) => c.grade_id === val);
                                    handleRelink(
                                      s.timing_id,
                                      r.stored_grade_id,
                                      val,
                                      target?.name || val,
                                    );
                                  }}
                                >
                                  <SelectTrigger
                                    className="h-8 text-xs"
                                    data-testid={`relink-select-${s.timing_id}-${r.stored_grade_id}`}
                                  >
                                    <SelectValue placeholder={
                                      cands.length === 0
                                        ? 'No live grades on this schedule'
                                        : 'Pick a grade…'
                                    } />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {cands.map((c) => (
                                      <SelectItem key={c.grade_id} value={c.grade_id}>
                                        {c.name}
                                        {c.discipline && (
                                          <span className="text-xs text-stone-500 ml-1.5">
                                            ({c.discipline})
                                          </span>
                                        )}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-1.5 px-2 text-right whitespace-nowrap">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-700 hover:text-red-800 hover:bg-red-50"
                                  disabled={busy}
                                  onClick={() => handleDelete(
                                    s.timing_id, r.stored_grade_id, rowLabel,
                                  )}
                                  data-testid={`delete-row-btn-${s.timing_id}-${r.stored_grade_id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewOpen(false)}
              disabled={repairing}
              data-testid="close-review-btn"
            >
              Close
            </Button>
            {autoMatchableCount > 0 && (
              <Button
                onClick={handleRepairAll}
                disabled={repairing}
                data-testid="apply-repair-btn"
              >
                <Link2 className="h-4 w-4 mr-1.5" />
                {repairing
                  ? 'Repairing…'
                  : `Auto-link ${autoMatchableCount} row(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
